import React, { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Camera,
  AlertCircle,
  Loader2,
  X,
  Factory,
  Cpu,
  ChevronDown,
  Image as ImageIcon,
  Upload,
  CheckCircle2,
} from "lucide-react";
import { machinesAPI, maintenanceAPI, errorAPI } from "../api";
import toast from "react-hot-toast";
import clsx from "clsx";

/**
 * Custom Dropdown styled strictly matching AddMachine & DocumentUpload design system
 */
const CustomSelect = ({
  value,
  onChange,
  options,
  placeholder = "Select an option...",
  icon: DefaultIcon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);
  const DisplayIcon = selectedOption?.icon || DefaultIcon;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={clsx(
          "w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border rounded-xl text-xs sm:text-sm text-slate-800 text-left flex items-center justify-between transition-all shadow-sm",
          isOpen
            ? "border-blue-500 ring-2 ring-blue-500/20 bg-white"
            : "border-slate-200"
        )}
      >
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
          {DisplayIcon && <DisplayIcon className="w-4 h-4" />}
        </span>

        <span
          className={clsx(
            "truncate font-medium pr-2",
            selectedOption ? "text-slate-800" : "text-slate-400"
          )}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>

        <ChevronDown
          className={clsx(
            "w-4 h-4 text-slate-400 transition-transform duration-200 flex-shrink-0",
            isOpen && "rotate-180 text-blue-600"
          )}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.99 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.99 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 z-50 mt-1.5 bg-white rounded-xl shadow-xl border border-slate-200/90 py-1.5 max-h-56 overflow-y-auto"
          >
            {options.map((option) => {
              const OptionIcon = option.icon;
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={clsx(
                    "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                    isSelected
                      ? "bg-blue-50 text-blue-700 font-semibold"
                      : "text-slate-700 hover:bg-slate-50"
                  )}
                >
                  <div className="flex items-center gap-2.5 truncate">
                    {OptionIcon && (
                      <OptionIcon
                        className={clsx(
                          "w-3.5 h-3.5 flex-shrink-0",
                          isSelected ? "text-blue-600" : "text-slate-400"
                        )}
                      />
                    )}
                    <span className="truncate">{option.label}</span>
                  </div>
                  {isSelected && (
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-600 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const LogError = ({ isOpen = true, onClose }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [description, setDescription] = useState("");
  const [selectedMachine, setSelectedMachine] = useState("");

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else {
      navigate("/errors");
    }
  }, [onClose, navigate]);

  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setPreview(null);
      setDescription("");
      setSelectedMachine("");
      setDragActive(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleClose]);

  const { data: rawMachines } = useQuery({
    queryKey: ["machines-list"],
    queryFn: () =>
      machinesAPI.getAll({ limit: 100 }).then((res) => {
        const raw = res.data?.data || res.data;
        return Array.isArray(raw) ? raw : [];
      }),
  });

  const machines = Array.isArray(rawMachines) ? rawMachines : [];

  const machineOptions = [
    { value: "", label: "Select Equipment / Machine...", icon: Cpu },
    ...machines.map((m) => ({
      value: m.id || m._id,
      label: `${m.name}${m.model ? ` (${m.model})` : ""}`,
      icon: Cpu,
    })),
  ];

  const uploadMutation = useMutation({
    mutationFn: (photoFile) => {
      const formData = new FormData();
      formData.append("image", photoFile);
      return maintenanceAPI.uploadImage(formData);
    },
  });

  const logMutation = useMutation({
    mutationFn: (data) => errorAPI.log(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["error-logs"] });
      toast.success("Error logged successfully! A service ticket has been created.");
      handleClose();
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || "Failed to report issue");
    },
  });

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleFile = (selectedFile) => {
    if (!selectedFile) return;

    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/tiff"];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(selectedFile.type)) {
      toast.error("Invalid file type. Please upload a PNG, JPG, or TIFF image.");
      return;
    }
    if (selectedFile.size > maxSize) {
      toast.error("File size must be less than 5MB.");
      return;
    }
    setFile(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
  };

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFile(e.dataTransfer.files[0]);
      }
    },
    [],
  );

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
    e.target.value = null;
  };

  const removeFile = () => {
    setFile(null);
    if (preview) {
      URL.revokeObjectURL(preview);
      setPreview(null);
    }
  };

  const isPending = logMutation.isPending || uploadMutation.isPending;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMachine) {
      toast.error("Please select an equipment / machine.");
      return;
    }
    if (!description.trim()) {
      toast.error("Please provide a description of the issue.");
      return;
    }

    try {
      let imageUrl = null;
      if (file) {
        const uploadRes = await toast.promise(uploadMutation.mutateAsync(file), {
          loading: "Uploading evidence photo...",
          success: "Photo uploaded!",
          error: "Photo upload failed.",
        });
        imageUrl = uploadRes?.data?.url || uploadRes?.url;
      }

      await logMutation.mutateAsync({
        machineId: selectedMachine,
        description: description.trim(),
        mediaUrls: imageUrl ? [imageUrl] : [],
      });
    } catch (err) {
      console.error("Submission failed:", err);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
        onClick={(e) => {
          if (e.target === e.currentTarget && !isPending) {
            handleClose();
          }
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="relative w-full max-w-xl sm:max-w-2xl bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col my-auto"
        >
          {/* Header matching AddMachine / DocumentUpload */}
          <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-100 bg-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 border border-rose-100/80 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">
                  Report Machine Issue
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Provide equipment error details and symptoms for fast maintenance resolution
                </p>
              </div>
            </div>
          </div>

          {/* Form Body */}
          <form onSubmit={handleSubmit} className="flex flex-col">
            <div className="p-5 sm:p-6 space-y-4 sm:space-y-5 overflow-y-auto max-h-[calc(85vh-130px)]">
              {/* Machine Selection with custom dropdown */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Equipment / Machine <span className="text-rose-500">*</span>
                </label>
                <CustomSelect
                  value={selectedMachine}
                  onChange={(val) => setSelectedMachine(val)}
                  options={machineOptions}
                  icon={Cpu}
                  placeholder="Select Equipment / Machine..."
                />
              </div>

              {/* Issue Description */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Issue Description <span className="text-rose-500">*</span>
                </label>
                <textarea
                  rows={4}
                  required
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the problem in detail (e.g. Error code E-402, spindle overheating, unusual vibration, when it started)..."
                  className="w-full px-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm resize-y min-h-[96px]"
                />
              </div>

              {/* Photo Upload Section */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Evidence Photo <span className="text-slate-400 font-normal">(Optional)</span>
                </label>

                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  className={clsx(
                    "p-5 border-2 border-dashed transition-all text-center rounded-2xl relative",
                    dragActive
                      ? "border-rose-500 bg-rose-50/70 shadow-sm"
                      : file
                        ? "border-emerald-300 bg-emerald-50/30"
                        : "border-slate-200 hover:border-slate-300 bg-slate-50/60"
                  )}
                >
                  <input
                    type="file"
                    id="log-error-photo-upload"
                    className="hidden"
                    accept="image/png, image/jpeg, image/tiff"
                    onChange={handleFileInput}
                  />
                  <label
                    htmlFor="log-error-photo-upload"
                    className="cursor-pointer block"
                  >
                    <div className="w-12 h-12 mx-auto mb-2.5 rounded-2xl bg-white border border-slate-200/80 flex items-center justify-center text-rose-500 shadow-xs">
                      <Camera className="w-5 h-5" />
                    </div>
                    <p className="text-sm font-semibold text-slate-800 mb-0.5">
                      {dragActive
                        ? "Drop the photo to attach"
                        : "Drag & drop a photo here, or browse"}
                    </p>
                    <p className="text-xs text-slate-400">
                      PNG, JPG, or TIFF • Max 5MB
                    </p>
                  </label>
                </div>

                {/* Selected Photo Preview Card */}
                {file && (
                  <div className="mt-3 p-3 sm:p-3.5 bg-slate-50 border border-slate-200 rounded-xl flex items-center gap-3">
                    {preview ? (
                      <img
                        src={preview}
                        alt="Evidence Preview"
                        className="w-10 h-10 rounded-lg object-cover border border-slate-200 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0">
                        <ImageIcon className="w-5 h-5" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900 text-xs sm:text-sm truncate">
                          {file.name}
                        </p>
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-100/80 px-1.5 py-0.5 rounded-md">
                          <CheckCircle2 className="w-3 h-3" />
                          Ready
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5 font-medium">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={removeFile}
                      className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-white rounded-lg transition-colors"
                      title="Remove photo"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 sm:p-5 bg-slate-50/80 border-t border-slate-100 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={handleClose}
                disabled={isPending}
                className="btn btn-secondary text-xs sm:text-sm py-2 px-3.5 sm:px-4 hover:border-slate-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isPending}
                className="btn btn-primary text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20 whitespace-nowrap"
              >
                {isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                    <span>Submitting Issue...</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4 mr-1.5" />
                    <span>Submit Error Log</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

export default LogError;
